import React, { useEffect, useState } from 'react';
import {
    Form,
    FormFeedback,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Spinner,
    Row,
    Col,
} from 'reactstrap';
import * as Yup from "yup";
import { useFormik } from "formik";
import { createOrder, updateOrder } from '../../api/orderApi';
import { getCampaigns } from '../../api/campaignApi';
import { getSites } from '../../api/siteApi';
import { getPlacements } from '../../api/placementApi';
import { getCreatives } from '../../api/creativeApi';
import { Campaign } from '../../types/campaign';
import { Site } from '../../types/site';
import { Placement } from '../../types/placement';
import { Creative } from '../../types/creative';
import { Order, AdType, PricingModel, AddOrder } from '../../types/order';
import { toast } from 'react-toastify';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { createOption } from '../../helpers/selectHelpers';
import selectSizeOptions from '../../constants/selectSizeOptions';

// Custom styles for react-select
const customSelectStyles = {
    control: (provided: any, state: any) => ({
        ...provided,
        minHeight: '38px',
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        },
    }),
    placeholder: (provided: any) => ({
        ...provided,
        color: '#6c757d',
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: '#212529',
    }),
};

interface AddEditOrderModalProps {
    isOpen: boolean;
    toggle: () => void;
    order?: Order | null;
    onOrderSaved?: () => void;
}

const AddEditOrderModal = ({ isOpen, toggle, order, onOrderSaved }: AddEditOrderModalProps) => {
    // local mutable options for creatable sizes
    const [sizeOptions, setSizeOptions] = useState(selectSizeOptions);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [sites, setSites] = useState<Site[]>([]);
    const [placements, setPlacements] = useState<Placement[]>([]);
    const [creatives, setCreatives] = useState<Creative[]>([]);
    const [filteredPlacements, setFilteredPlacements] = useState<Placement[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshingCampaigns, setRefreshingCampaigns] = useState<boolean>(false);
    const [refreshingSites, setRefreshingSites] = useState<boolean>(false);
    const [refreshingCreatives, setRefreshingCreatives] = useState<boolean>(false);
    const [refreshingPlacements, setRefreshingPlacements] = useState<boolean>(false);

    // Convert data to react-select options
    const campaignOptions = campaigns.map(campaign => ({
        value: campaign.id,
        label: campaign.name
    }));

    const siteOptions = sites.map(site => ({
        value: site.id,
        label: site?.site || site?.app || 'Sem nome'
    }));

    const placementOptions = filteredPlacements.map(placement => ({
        value: placement.id,
        label: placement.name
    }));

    const creativeOptions = creatives.map(creative => ({
        value: creative.id,
        label: creative.name
    }));

    const adTypeOptions = [
        { value: AdType.DISPLAY, label: 'Display' },
        { value: AdType.VIDEO, label: 'Vídeo' },
        { value: AdType.AUDIO, label: 'Áudio' },
    ];

    const pricingModelOptions = [
        { value: PricingModel.CPM, label: 'CPM - Custo por mil impressões' },
        { value: PricingModel.CPC, label: 'CPC - Custo por clique' },
        { value: PricingModel.CPV, label: 'CPV - Custo por visualização' },
    ];

    useEffect(() => {
        // Load all required data
        const loadData = async () => {
            try {
                const [campaignsData, sitesData, placementsData, creativesData] = await Promise.all([
                    getCampaigns(),
                    getSites(),
                    getPlacements(),
                    getCreatives(),
                ]);
                setCampaigns(campaignsData);
                setSites(sitesData);
                setPlacements(placementsData);
                setCreatives(creativesData);
            } catch (error) {
                console.error('Error loading data:', error);
                toast.error('Erro ao carregar dados');
            }
        };
        loadData();
    }, []);

    // Refresh functions
    const refreshCampaigns = async () => {
        setRefreshingCampaigns(true);
        try {
            const campaignsData = await getCampaigns();
            setCampaigns(campaignsData);
            toast.success('Campanhas atualizadas com sucesso!');
        } catch (error) {
            console.error('Error refreshing campaigns:', error);
            toast.error('Erro ao atualizar campanhas');
        } finally {
            setRefreshingCampaigns(false);
        }
    };

    const refreshSites = async () => {
        setRefreshingSites(true);
        try {
            const sitesData = await getSites();
            setSites(sitesData);
            toast.success('Sites atualizados com sucesso!');
        } catch (error) {
            console.error('Error refreshing sites:', error);
            toast.error('Erro ao atualizar sites');
        } finally {
            setRefreshingSites(false);
        }
    };

    const refreshCreatives = async () => {
        setRefreshingCreatives(true);
        try {
            const creativesData = await getCreatives();
            setCreatives(creativesData);
            toast.success('Criativos atualizados com sucesso!');
        } catch (error) {
            console.error('Error refreshing creatives:', error);
            toast.error('Erro ao atualizar criativos');
        } finally {
            setRefreshingCreatives(false);
        }
    };

    const refreshPlacements = async () => {
        setRefreshingPlacements(true);
        try {
            const placementsData = await getPlacements();
            setPlacements(placementsData);
            toast.success('Placements atualizados com sucesso!');
        } catch (error) {
            console.error('Error refreshing placements:', error);
            toast.error('Erro ao atualizar placements');
        } finally {
            setRefreshingPlacements(false);
        }
    };

    const validationSchema = Yup.object({
        campaignId: Yup.number().required("Campanha é obrigatória"),
        siteId: Yup.number().optional(),
        startDate: Yup.string().required("Data de início é obrigatória"),
        endDate: Yup.string().required("Data de término é obrigatória"),
        adType: Yup.string().required("Tipo de anúncio é obrigatório"),
        viewability: Yup.number()
            .min(0, "Visibilidade deve ser entre 0 e 100")
            .max(100, "Visibilidade deve ser entre 0 e 100")
            .required("Visibilidade é obrigatória"),
        placementId: Yup.number().integer(),
        sizes: Yup.array().min(1, "Pelo menos um tamanho deve ser selecionado").test('all-valid-sizes', 'Tamanho inválido', (arr) => {
            if (!arr || arr.length === 0) return false;
            return arr.every((v: any) => v === 'fluid' || /^\d+x\d+$/.test(v));
        }),
        pricingModel: Yup.string().required("Modelo de preço é obrigatório"),
        maxMediaDurationInSeconds: Yup.number().when('adType', {
            is: (adType: string) => adType === AdType.AUDIO || adType === AdType.VIDEO,
            then: (schema) => schema.required("Duração máxima é obrigatória para áudio/vídeo"),
            otherwise: (schema) => schema.optional(),
        }),
        impressions: Yup.number()
            .min(1, "Impressões deve ser maior que 0")
            .required("Impressões é obrigatório"),
        clickTag: Yup.string().test('valid-url', 'URL inválida', (value) => {
            if (!value) return false;
            try {
                new URL(value);
                return true;
            } catch {
                return false;
            }
        }).required("Click tag é obrigatório"),
        segmentation: Yup.string(),
        creativeLine: Yup.string(),
        creativeId: Yup.number().optional().nullable(),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            campaignId: order?.campaign?.id || '',
            siteId: order?.site?.id || '',
            startDate: order?.startDate ? order.startDate.split('T')[0] : '',
            endDate: order?.endDate ? order.endDate.split('T')[0] : '',
            adType: order?.adType || '',
            segmentation: order?.segmentation || '',
            creativeLine: order?.creativeLine || '',
            viewability: order?.viewability || 0,
            placementId: order?.placement?.id || '',
            sizes: order?.sizes || [],
            pricingModel: order?.pricingModel || '',
            maxMediaDurationInSeconds: order?.maxMediaDurationInSeconds || '',
            impressions: order?.impressions || 0,
            clickTag: order?.clickTag || '',
            creativeId: order?.creative?.id || null,
        },
        validationSchema,
        validate: (values) => {
            console.log('Form validation triggered with values:', values);
        },
        onSubmit: async (values) => {
            console.log('Form submitted with values:', values);
            console.log('Form errors:', formik.errors);
            console.log('Form touched:', formik.touched);
            
            setLoading(true);
            try {
                const orderData: AddOrder = {
                    campaignId: values.campaignId ? Number(values.campaignId) : null,
                    siteId: values.siteId ? Number(values.siteId) : null,
                    startDate: values.startDate,
                    endDate: values.endDate,
                    adType: values.adType as AdType,
                    segmentation: values.segmentation,
                    creativeLine: values.creativeLine,
                    viewability: values.viewability,
                    placementId: values.placementId ? Number(values.placementId) : null,
                    sizes: values.sizes,
                    pricingModel: values.pricingModel as PricingModel,
                    maxMediaDurationInSeconds: values.maxMediaDurationInSeconds ? Number(values.maxMediaDurationInSeconds) : null,
                    impressions: values.impressions,
                    clickTag: values.clickTag,
                    creativeId: values.creativeId ? Number(values.creativeId) : null,
                };

                if (order) {
                    await updateOrder(order.id, orderData);
                    toast.success('Pedido atualizado com sucesso!');
                } else {
                    await createOrder(orderData);
                    toast.success('Pedido criado com sucesso!');
                }

                toggle();
                onOrderSaved?.();
            } catch (error) {
                console.error('Error saving order:', error);
                toast.error('Erro ao salvar pedido');
            } finally {
                setLoading(false);
            }
        },
    });

    const toggleModal = () => {
        toggle();
        formik.resetForm();
    };

    // Filter placements when site changes
    useEffect(() => {
        if (formik.values.siteId && placements.length > 0) {
            const filtered = placements.filter(placement => placement.site.id === formik.values.siteId);
            setFilteredPlacements(filtered);
        } else {
            setFilteredPlacements([]);
        }
    }, [formik.values.siteId, placements]);

    // Ensure order existing sizes are present in local options when editing
    useEffect(() => {
        if (order && Array.isArray(order.sizes)) {
            order.sizes.forEach((s) => {
                if (!sizeOptions.find(o => o.value === s)) {
                    setSizeOptions(prev => [...prev, createOption(s, s)]);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);

    return (
        <Modal isOpen={isOpen} toggle={toggleModal} size="xl" centered>
            <ModalHeader toggle={toggleModal}>
                {order ? 'Editar Pedido' : 'Adicionar Pedido'}
            </ModalHeader>
            <ModalBody>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formik.handleSubmit();
                return false;
            }}>
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Label htmlFor="campaignId" className="form-label mb-0">
                                        Campanha *
                                    </Label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={refreshCampaigns}
                                        disabled={refreshingCampaigns}
                                        title="Atualizar lista de campanhas"
                                    >
                                        {refreshingCampaigns ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <i className="ri-refresh-line"></i>
                                        )}
                                    </button>
                                </div>
                                <Select
                                    options={campaignOptions}
                                    value={campaignOptions.find(option => option.value === formik.values.campaignId)}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue('campaignId', selectedOption?.value || '');
                                    }}
                                    onBlur={() => formik.setFieldTouched('campaignId', true)}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Selecione uma campanha"
                                    isClearable
                                    styles={customSelectStyles}
                                />
                                {formik.touched.campaignId && formik.errors.campaignId && (
                                    <div className="invalid-feedback d-block">{formik.errors.campaignId}</div>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Label htmlFor="siteId" className="form-label mb-0">
                                        Site
                                    </Label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={refreshSites}
                                        disabled={refreshingSites}
                                        title="Atualizar lista de sites"
                                    >
                                        {refreshingSites ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <i className="ri-refresh-line"></i>
                                        )}
                                    </button>
                                </div>
                                <Select
                                    options={siteOptions}
                                    value={siteOptions.find(option => option.value === formik.values.siteId)}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue('siteId', selectedOption?.value || '');
                                        formik.setFieldValue('placementId', ''); // Reset placement when site changes
                                    }}
                                    onBlur={() => formik.setFieldTouched('siteId', true)}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Selecione um site"
                                    isClearable
                                    styles={customSelectStyles}
                                />
                                {formik.touched.siteId && formik.errors.siteId && (
                                    <div className="invalid-feedback d-block">{formik.errors.siteId}</div>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Label htmlFor="placementId" className="form-label mb-0">
                                        Placement
                                    </Label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={refreshPlacements}
                                        disabled={refreshingPlacements}
                                        title="Atualizar lista de placements"
                                    >
                                        {refreshingPlacements ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <i className="ri-refresh-line"></i>
                                        )}
                                    </button>
                                </div>
                                <Select
                                    options={placementOptions}
                                    value={placementOptions.find(option => option.value === formik.values.placementId)}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue('placementId', selectedOption?.value || '');
                                    }}
                                    onBlur={() => formik.setFieldTouched('placementId', true)}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Selecione um placement"
                                    isClearable
                                    isDisabled={!formik.values.siteId}
                                    noOptionsMessage={() => "Selecione um site primeiro"}
                                    styles={customSelectStyles}
                                />
                                {formik.touched.placementId && formik.errors.placementId && (
                                    <div className="invalid-feedback d-block">{formik.errors.placementId}</div>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="startDate" className="form-label">
                                    Data de Início *
                                </Label>
                                <Input
                                    type="date"
                                    id="startDate"
                                    name="startDate"
                                    value={formik.values.startDate}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.startDate && formik.errors.startDate)}
                                />
                                {formik.touched.startDate && formik.errors.startDate && (
                                    <FormFeedback type="invalid">{formik.errors.startDate}</FormFeedback>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="endDate" className="form-label">
                                    Data de Término *
                                </Label>
                                <Input
                                    type="date"
                                    id="endDate"
                                    name="endDate"
                                    value={formik.values.endDate}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.endDate && formik.errors.endDate)}
                                />
                                {formik.touched.endDate && formik.errors.endDate && (
                                    <FormFeedback type="invalid">{formik.errors.endDate}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="adType" className="form-label">
                                    Tipo de Anúncio *
                                </Label>
                                <Select
                                    options={adTypeOptions}
                                    value={adTypeOptions.find(option => option.value === formik.values.adType)}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue('adType', selectedOption?.value || '');
                                    }}
                                    onBlur={() => formik.setFieldTouched('adType', true)}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Selecione o tipo"
                                    isClearable
                                    styles={customSelectStyles}
                                />
                                {formik.touched.adType && formik.errors.adType && (
                                    <div className="invalid-feedback d-block">{formik.errors.adType}</div>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="pricingModel" className="form-label">
                                    Modelo de Preço *
                                </Label>
                                <Select
                                    options={pricingModelOptions}
                                    value={pricingModelOptions.find(option => option.value === formik.values.pricingModel)}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue('pricingModel', selectedOption?.value || '');
                                    }}
                                    onBlur={() => formik.setFieldTouched('pricingModel', true)}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Selecione o modelo"
                                    isClearable
                                    styles={customSelectStyles}
                                />
                                {formik.touched.pricingModel && formik.errors.pricingModel && (
                                    <div className="invalid-feedback d-block">{formik.errors.pricingModel}</div>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="segmentation" className="form-label">
                                    Segmentação
                                </Label>
                                <Input
                                    type="text"
                                    id="segmentation"
                                    name="segmentation"
                                    value={formik.values.segmentation}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.segmentation && formik.errors.segmentation)}
                                />
                                {formik.touched.segmentation && formik.errors.segmentation && (
                                    <FormFeedback type="invalid">{formik.errors.segmentation}</FormFeedback>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="creativeLine" className="form-label">
                                    Linha Criativa
                                </Label>
                                <Input
                                    type="text"
                                    id="creativeLine"
                                    name="creativeLine"
                                    value={formik.values.creativeLine}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.creativeLine && formik.errors.creativeLine)}
                                />
                                {formik.touched.creativeLine && formik.errors.creativeLine && (
                                    <FormFeedback type="invalid">{formik.errors.creativeLine}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="viewability" className="form-label">
                                    Visibilidade (%) *
                                </Label>
                                <Input
                                    type="number"
                                    id="viewability"
                                    name="viewability"
                                    min="0"
                                    max="100"
                                    value={formik.values.viewability}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.viewability && formik.errors.viewability)}
                                />
                                {formik.touched.viewability && formik.errors.viewability && (
                                    <FormFeedback type="invalid">{formik.errors.viewability}</FormFeedback>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="impressions" className="form-label">
                                    Impressões *
                                </Label>
                                <Input
                                    type="number"
                                    id="impressions"
                                    name="impressions"
                                    min="1"
                                    value={formik.values.impressions}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.impressions && formik.errors.impressions)}
                                />
                                {formik.touched.impressions && formik.errors.impressions && (
                                    <FormFeedback type="invalid">{formik.errors.impressions}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    {(formik.values.adType === AdType.AUDIO || formik.values.adType === AdType.VIDEO) && (
                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label htmlFor="maxMediaDurationInSeconds" className="form-label">
                                        Duração Máxima (segundos) *
                                    </Label>
                                    <Input
                                        type="number"
                                        id="maxMediaDurationInSeconds"
                                        name="maxMediaDurationInSeconds"
                                        min="1"
                                        value={formik.values.maxMediaDurationInSeconds}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        invalid={!!(formik.touched.maxMediaDurationInSeconds && formik.errors.maxMediaDurationInSeconds)}
                                    />
                                    {formik.touched.maxMediaDurationInSeconds && formik.errors.maxMediaDurationInSeconds && (
                                        <FormFeedback type="invalid">{formik.errors.maxMediaDurationInSeconds}</FormFeedback>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="sizes" className="form-label">
                                    Tamanhos *
                                </Label>
                                <CreatableSelect
                                    isMulti
                                    options={sizeOptions}
                                    value={sizeOptions.filter(option => formik.values.sizes.includes(option.value))}
                                    onChange={(selectedOptions) => {
                                        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                                        formik.setFieldValue('sizes', values);
                                    }}
                                    onCreateOption={(inputValue) => {
                                        const isValid = inputValue === 'fluid' || /^\d+x\d+$/.test(inputValue);
                                        if (!isValid) {
                                            toast.error('Tamanho inválido. Use o formato {largura}x{altura}, ex: 300x250');
                                            return;
                                        }
                                        const newOpt = createOption(inputValue, inputValue);
                                        setSizeOptions(prev => (prev.find(o => o.value === inputValue) ? prev : [...prev, newOpt]));
                                        formik.setFieldValue('sizes', [...formik.values.sizes, inputValue]);
                                    }}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Selecione os tamanhos"
                                    styles={customSelectStyles}
                                />
                                {formik.touched.sizes && formik.errors.sizes && (
                                    <div className="invalid-feedback d-block">{formik.errors.sizes}</div>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="clickTag" className="form-label">
                                    Click Tag (URL) *
                                </Label>
                                <Input
                                    type="url"
                                    id="clickTag"
                                    name="clickTag"
                                    value={formik.values.clickTag}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={!!(formik.touched.clickTag && formik.errors.clickTag)}
                                    placeholder="https://exemplo.com"
                                />
                                {formik.touched.clickTag && formik.errors.clickTag && (
                                    <FormFeedback type="invalid">{formik.errors.clickTag}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Label htmlFor="creativeId" className="form-label mb-0">
                                        Criativo
                                    </Label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={refreshCreatives}
                                        disabled={refreshingCreatives}
                                        title="Atualizar lista de criativos"
                                    >
                                        {refreshingCreatives ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <i className="ri-refresh-line"></i>
                                        )}
                                    </button>
                                </div>
                                <Select
                                    options={creativeOptions}
                                    value={creativeOptions.find(option => option.value === formik.values.creativeId)}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue('creativeId', selectedOption?.value || '');
                                    }}
                                    onBlur={() => formik.setFieldTouched('creativeId', true)}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Selecione um criativo"
                                    isClearable
                                    styles={customSelectStyles}
                                />
                                {formik.touched.creativeId && formik.errors.creativeId && (
                                    <div className="invalid-feedback d-block">{formik.errors.creativeId}</div>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={toggleModal}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading && <Spinner size="sm" className="me-2" />}
                            {order ? 'Atualizar' : 'Criar'} Pedido
                        </button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default AddEditOrderModal;
